import React, { useState } from "react";
import { Modal, Input, Table, notification, Spin } from "antd";
import { LuX, LuSearch } from "react-icons/lu";
import { SlidersHorizontal } from "lucide-react";
import {
  useCreateQuestionMutation,
  useGetQuestionTemplatesQuery,
} from "../../store/api/apiSlice";
// import { useCreateQuestionMutation, useGetQuestionTemplatesQuery } from '../../../store/api/apiSlice';

const TemplateQuestionModal = ({
  visible,
  onClose,
  formId,
  existingQuestions = [],
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [creating, setCreating] = useState(false);

  // Fetch question templates
  const { data: questionTemplates = [], isLoading } =
    useGetQuestionTemplatesQuery();
  const [createQuestion] = useCreateQuestionMutation();
  // Filter out already added questions and handle search
  const filteredTemplates = questionTemplates.filter((template) => {
    const matchesSearch =
      template.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchText.toLowerCase());
    const isNotAdded = !existingQuestions.some(
      (eq) => eq.title === template.title
    );
    return matchesSearch && isNotAdded;
  });

  const columns = [
    {
      title: "Sr#",
      dataIndex: "id",
      key: "id",
      width: 70,
      render: (_, __, index) => (
        <span className="text-sm text-gray-500 pl-4">{index + 1}</span>
      ),
    },
    {
      title: "Question",
      key: "question",
      render: (_, record) => (
        <div className="flex items-start gap-4 py-4">
          <div className="relative w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
            {record.video && (
              <video
                src={record.video}
                alt=""
                controls
                className="w-full h-full object-cover rounded-lg"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-10 w-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 3L19 12L5 21V3Z" fill="white" />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-md font-medium mb-0">{record.title}</h3>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
              {record.description}
            </p>
          </div>
        </div>
      ),
    },
  ];

  const handleSelect = async (record) => {
    try {
      setCreating(true);

      // Create a copy of the template data without id and is_template
      const { _id, is_template, ...questionData } = record;

      // Calculate the next question number (max existing number + 1)
      const nextNumber =
        existingQuestions.length > 0
          ? Math.max(...existingQuestions.map((q) => q.number || 0)) + 1
          : 0;

      // Add the proper number to the question data
      const questionWithNumber = {
        ...questionData,
        number: nextNumber, // This ensures it's added at the end
      };

      // Add the question
      await createQuestion({
        formId,
        data: questionWithNumber,
      }).unwrap();

      // Update selected questions
      setSelectedQuestions([...selectedQuestions, record._id]);
      onClose();
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to add question",
      });
    } finally {
      setCreating(false);
    }
  };

  if (isLoading) {
    return <Spin />;
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      title="Choose Template Question"
      closeIcon={
        <div className="bg-gray-100 h-8 w-8 flex items-center justify-center rounded-full">
          <LuX className="text-red-500" size={20} />
        </div>
      }
    >
      <div className="mt-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              placeholder="Search..."
              className="py-2 rounded-full border"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<LuSearch className="text-gray-400" />}
              suffix={
                searchQuery && (
                  <LuX
                    className="cursor-pointer text-gray-400"
                    size={16}
                    onClick={() => setSearchQuery("")}
                  />
                )
              }
            />
          </div>
          <button className="h-10 w-10 flex items-center justify-center rounded-full border border-gray-200">
            <SlidersHorizontal size={20} className="text-red-500" />
          </button>
        </div>

        <div className="mt-6">
          <Table
            columns={columns}
            dataSource={filteredTemplates}
            rowClassName={(record) =>
              selectedQuestions.includes(record._id) ? "bg-gray-50" : ""
            }
            onRow={(record) => ({
              onClick: () =>
                !selectedQuestions.includes(record._id) && handleSelect(record),
              className: `cursor-pointer ${
                selectedQuestions.includes(record._id) ? "opacity-50" : ""
              }`,
            })}
            loading={creating}
          />
        </div>
      </div>
    </Modal>
  );
};

export default TemplateQuestionModal;
